import React, { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { api } from "../lib/api";
import { formatDateDisplay, formatPlatform } from "../lib/utils";
import { Search, Package, Truck, CheckCircle2, XCircle, Clock, ExternalLink, MessageSquare, Tag } from "lucide-react";
import PageHero from "../components/PageHero";

const STATUS_STEPS = ["placed", "processing", "shipped", "delivered"];

const statusIcon = {
  placed: Clock,
  processing: Package,
  shipped: Truck,
  delivered: CheckCircle2,
  cancelled: XCircle,
};

const statusColor = {
  placed: "text-gold",
  processing: "text-sky-400",
  shipped: "text-violet-400",
  delivered: "text-emerald-400",
  cancelled: "text-rose-400",
};

export default function TrackOrderPage() {
  const [searchParams] = useSearchParams();
  const [input, setInput] = useState(searchParams.get("q") || "");
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const q = searchParams.get("q");
    if (q) {
      setInput(q);
      api.get(`/orders/${q.trim()}`).then((r) => setOrder(r.data)).catch(() => setError("Order not found."));
    }
  }, [searchParams]);

  const search = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    setLoading(true);
    setError("");
    setOrder(null);
    try {
      const r = await api.get(`/orders/${input.trim()}`);
      setOrder(r.data);
    } catch {
      setError("Order not found. Check your order number and try again.");
    } finally {
      setLoading(false);
    }
  };

  const stepIdx = order ? STATUS_STEPS.indexOf(order.status) : -1;
  const formatMoney = (value) => value.toLocaleString("en-IN", { maximumFractionDigits: 2 });

  return (
    <div>
      <Helmet><meta name="robots" content="noindex, nofollow" /></Helmet>
      <PageHero
        eyebrow="Delivery"
        title={<>Track <span className="text-gold">order</span></>}
        breadcrumbs={[{ to: "/", label: "Home" }, { label: "Track Order" }]}
        size="compact"
      />
      <div className="site-container py-12">

      <form onSubmit={search} className="flex gap-2 mb-10">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gold" />
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Enter order number e.g. GT260501ABC123"
            className="w-full bg-navy-800 border border-gold/20 rounded-full pl-10 pr-4 py-3 text-sm placeholder:text-slate-500 focus:outline-none focus:border-gold/60 transition"
          />
        </div>
        <button
          type="submit"
          disabled={loading}
          className="bg-gold text-navy-900 font-extrabold uppercase tracking-wider text-sm px-6 py-3 rounded-full hover:bg-gold-light btn-glow transition-all disabled:opacity-50"
        >
          {loading ? "…" : "Track"}
        </button>
      </form>

      {error && (
        <div className="bg-rose-500/10 border border-rose-500/30 rounded-xl px-5 py-4 text-sm text-rose-300">{error}</div>
      )}

      {order && (        <div className="bg-navy-700 rounded-2xl neon-border p-6 space-y-6">
          <div className="flex justify-between items-start">
            <div>
              <div className="text-xs uppercase tracking-wider text-gold mb-1">Order</div>
              <div className="text-2xl font-black">#{order.order_number}</div>
              <div className="text-xs text-slate-400 mt-1">{formatDateDisplay(order.created_at)}</div>
            </div>
            {(() => {
              const Icon = statusIcon[order.status] || Clock;
              return (
                <div className={`flex items-center gap-2 text-sm font-bold uppercase tracking-wider ${statusColor[order.status] || "text-gold"}`}>
                  <Icon className="w-5 h-5" />
                  {order.status}
                </div>
              );
            })()}
          </div>

          {order.status !== "cancelled" && (
            <div className="relative">
              <div className="flex justify-between mb-2">
                {STATUS_STEPS.map((s, i) => (
                  <div key={s} className="flex flex-col items-center gap-1 flex-1">
                    <div className={`w-7 h-7 rounded-full border-2 flex items-center justify-center text-xs font-black transition-all ${i <= stepIdx ? "bg-gold border-gold text-navy-900" : "border-gold/20 text-slate-600"}`}>
                      {i + 1}
                    </div>
                    <div className={`text-[9px] uppercase tracking-wider ${i <= stepIdx ? "text-gold" : "text-slate-600"}`}>{s}</div>
                  </div>
                ))}
              </div>
              <div className="absolute top-3.5 left-[10%] right-[10%] h-0.5 bg-gold/10 -z-0">
                <div
                  className="h-full bg-gold transition-all duration-700"
                  style={{ width: stepIdx < 0 ? "0%" : `${(stepIdx / (STATUS_STEPS.length - 1)) * 100}%` }}
                />
              </div>
            </div>
          )}

          <div className="border-t border-gold/20 pt-4 space-y-2">
            <div className="text-xs uppercase tracking-wider text-gold mb-2">Items</div>
            {order.items.map((it, i) => (
              <Link key={i} to={`/product/${it.product_id}`} className="flex gap-3 items-center text-sm hover:bg-navy-800/60 rounded-lg px-2 py-1 -mx-2 transition">
                {it.image && <img src={it.image} alt="" className="w-10 h-10 object-cover rounded" />}
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate hover:text-gold transition">{it.name}</div>
                  <div className="text-xs text-slate-400">
                    Qty {it.quantity}
                    {it.platform && <span className="ml-1">· {formatPlatform(it.platform)}</span>}
                    {it.condition && <span className="ml-1">· {it.condition.charAt(0).toUpperCase() + it.condition.slice(1)}</span>}
                  </div>
                </div>
                <div className="font-bold text-gold">₹{(it.price * it.quantity).toLocaleString()}</div>
              </Link>
            ))}
          </div>

          {order.order_type === "preorder" ? (
            <div className="border-t border-gold/20 pt-4 space-y-3">
              {/* Customer details */}
              <div className="text-xs uppercase tracking-wider text-gold mb-2">Customer Details</div>
              <div className="bg-navy-800/60 rounded-xl px-4 py-3 space-y-1 text-sm">
                <div className="flex justify-between"><span className="text-slate-400">Name</span><span className="font-semibold">{order.customer_name}</span></div>
                <div className="flex justify-between"><span className="text-slate-400">Phone</span><span>{order.customer_phone}</span></div>
                {order.customer_email && <div className="flex justify-between"><span className="text-slate-400">Email</span><span>{order.customer_email}</span></div>}
                {order.delivery_required != null && (
                  <div className="flex justify-between">
                    <span className="text-slate-400">Delivery</span>
                    <span>{!order.delivery_required ? "Not required" : order.delivery_included ? <span className="text-emerald-400">Required — Included</span> : <span className="text-sky-300">Required — Extra charge</span>}</span>
                  </div>
                )}
              </div>

              {/* Pricing breakdown */}
              <div className="text-xs uppercase tracking-wider text-gold mb-2 pt-1">Pricing</div>
              <div className="space-y-1 text-sm">
                {(() => {
                  const mrp = order.subtotal; // product price × qty
                  const offered = order.price_offered;
                  const booked = order.booking_amount;
                  const discount = offered != null && mrp > offered ? mrp - offered : null;
                  return (
                    <>
                      <div className="flex justify-between"><span className="text-slate-400">MRP</span><span>₹{formatMoney(mrp)}</span></div>
                      {discount != null && (
                        <div className="flex justify-between text-emerald-400">
                          <span>Discount offered</span>
                          <span>−₹{formatMoney(discount)}</span>
                        </div>
                      )}
                      {offered != null && (
                        <div className="flex justify-between font-bold border-t border-gold/20 pt-1 mt-1">
                          <span>Agreed Price</span>
                          <span className="text-gold">₹{formatMoney(offered)}</span>
                        </div>
                      )}
                      {booked != null && (
                        <div className="flex justify-between text-slate-300"><span className="text-slate-400">Booking Amount Paid</span><span>₹{formatMoney(booked)}</span></div>
                      )}
                      {offered != null && booked != null && (
                        <div className="flex justify-between font-black text-base mt-1 border-t border-gold/20 pt-2">
                          <span>Balance Due</span>
                          <span className="text-gold">₹{formatMoney(offered - booked)}</span>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          ) : (
            <div className="border-t border-gold/20 pt-4 space-y-1 text-sm">
              <div className="flex justify-between"><span className="text-slate-400">Subtotal</span><span>₹{formatMoney(order.subtotal)}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Shipping</span><span>{order.shipping === 0 ? "FREE" : `₹${order.shipping}`}</span></div>
              {(order.processing_fee || 0) > 0 && (
                <div className="flex justify-between">
                  <span className="text-slate-400">Digital processing (2.5%)</span>
                  <span>₹{formatMoney(order.processing_fee)}</span>
                </div>
              )}
              {order.coupon_code && (
                <div className="flex justify-between text-emerald-400">
                  <span className="flex items-center gap-1.5"><Tag className="w-3.5 h-3.5" />{order.coupon_code} applied</span>
                  <span>−₹{formatMoney(order.coupon_discount || 0)}</span>
                </div>
              )}
              <div className="flex justify-between font-black text-base mt-1"><span>Total</span><span className="text-gold">₹{formatMoney(order.total)}</span></div>
            </div>
          )}

          {order.order_type !== "preorder" && order.shipping_address && (
          <div className="border-t border-gold/20 pt-4 text-xs text-slate-400">
            <div className="font-semibold text-slate-300 mb-0.5">Shipping to</div>
            {order.shipping_address}, {order.city}, {order.state} — {order.pincode}
          </div>
          )}

          {/* Tracking link */}
          {order.tracking_link && (
            <div className="border-t border-gold/20 pt-4">
              <a
                href={order.tracking_link}
                target="_blank"
                rel="noreferrer"
                className="flex items-center justify-center gap-2 w-full bg-violet-500/10 border border-violet-500/30 text-violet-300 font-bold uppercase tracking-wider text-xs py-3 rounded-full hover:bg-violet-500/20 transition"
              >
                <Truck className="w-4 h-4" />
                Track Shipment <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
          )}

          {/* Admin notes */}
          {order.admin_notes && (
            <div className="border-t border-gold/20 pt-4">
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-gold mb-2">
                <MessageSquare className="w-3.5 h-3.5" /> Store Update
              </div>
              <p className="text-sm text-slate-300 bg-gold/5 border border-gold/15 rounded-xl px-4 py-3 leading-relaxed">
                {order.admin_notes}
              </p>
            </div>
          )}
        </div>
      )}
      </div>
    </div>
  );
}
